/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Datacenter } from "../DatacenterData";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { GenericTable} from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconCheck } from "../../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../../shared/assets/Icons/IconCrossSquare.svg";
import { getProviders } from "../request/request";

interface InfluxKey {
  id: number;
  keyInflux: string;
  isActive: boolean;
}

export interface DatacenterEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  datacenter: Datacenter | null;
  onClose: () => void;
  onSave: (updatedDatacenter: Partial<Datacenter>) => Promise<void>;
}

export const DatacenterEditModal: React.FC<DatacenterEditModalProps> = ({
  isOpen,
  isLoading,
  datacenter,
  onClose,
  onSave,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  
  // Form state
  const [formData, setFormData] = useState<Partial<Datacenter>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [providers, setProviders] = useState<Array<{ id: number; name: string; idAddon: string }>>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [selectedProviderIdAddon, setSelectedProviderIdAddon] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Influx Keys state for editing
  const [influxKeys, setInfluxKeys] = useState<InfluxKey[]>([]);
  const [editingInfluxKeyId, setEditingInfluxKeyId] = useState<number | null>(null);
  const [editingInfluxKeyData, setEditingInfluxKeyData] = useState<InfluxKey | null>(null);
  const [nextTempId, setNextTempId] = useState<number>(-1);

  // Fetch providers when modal opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      (async () => {
        try {
          setIsLoadingProviders(true);
          const token = await getAccessTokenSilently();
          const resp = await getProviders(token);
          const providersData = (resp as any)?.data?.data ?? [];
          setProviders(providersData);
        } catch (error) {
          console.error("Error fetching providers:", error);
          setProviders([]);
        } finally {
          setIsLoadingProviders(false);
        }
      })();
    }
  }, [isOpen, isAuthenticated, getAccessTokenSilently]);

  // Initialize form data when datacenter changes
  useEffect(() => {
    if (datacenter) {
      setFormData({
        datacenterId: datacenter.datacenterId || "",
        name: datacenter.name || "",
        displayName: datacenter.displayName || "",
        direction: datacenter.direction || "",
        gps: datacenter.gps || "",
        capacityKW: datacenter.capacityKW || "",
        phase: datacenter.phase || "",
        cage: datacenter.cage || "",
        rackId: datacenter.rackId || "",
        rackCapacity: datacenter.rackCapacity || "",
        patchPanelODF: datacenter.patchPanelODF || "",
        rackSecurityPin: datacenter.rackSecurityPin || "",
        status: datacenter.status || "Active",
        visible: datacenter.visible ?? true,
        providerCode: datacenter.providerCode || "",
        city: datacenter.city || "",
        providerId: datacenter.providerId || 0,
      });
      
      // Initialize provider selection from datacenter
      if (datacenter.provider) {
        setSelectedProvider(datacenter.provider.name || "");
        // Use data from datacenter as initial values
        if (datacenter.provider.id && datacenter.provider.idAddon) {
          setSelectedProviderId(datacenter.provider.id);
          setSelectedProviderIdAddon(datacenter.provider.idAddon);
        }
      }
      setErrorMessage(null);
      
      // Initialize Influx Keys state
      if (datacenter.influxKeys) {
        setInfluxKeys(datacenter.influxKeys);
      } else {
        setInfluxKeys([]);
      }
      // Reset editing state
      setEditingInfluxKeyId(null);
      setEditingInfluxKeyData(null);
      setNextTempId(-1);
    }
  }, [datacenter]);

  // Update provider id and idAddon when providers load and selectedProvider is set
  useEffect(() => {
    if (selectedProvider && providers.length > 0) {
      const provider = providers.find((p) => p.name === selectedProvider);
      if (provider) {
        setSelectedProviderId(provider.id);
        setSelectedProviderIdAddon(provider.idAddon);
      }
    }
  }, [providers, selectedProvider]);

  const handleInputChange = (field: keyof Datacenter, value: string | boolean | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStatusToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      status: checked ? "Active" : "Inactive",
    }));
  };

  const handleVisibleToggle = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      visible: checked,
    }));
  };

  const handleProviderSelect = (providerName: string) => {
    setSelectedProvider(providerName);
    // Find the selected provider object to get id and idAddon
    const provider = providers.find((p) => p.name === providerName);
    if (provider) {
      setSelectedProviderId(provider.id);
      setSelectedProviderIdAddon(provider.idAddon);
    } else {
      setSelectedProviderId(null);
      setSelectedProviderIdAddon("");
    }
  };

  // Influx Keys handlers
  const handleEditInfluxKey = (influxKeyId: number) => {
    const influxKey = influxKeys.find(ik => ik.id === influxKeyId);
    if (influxKey) {
      setEditingInfluxKeyId(influxKeyId);
      setEditingInfluxKeyData({ ...influxKey });
    }
  };

  const handleSaveInfluxKey = (influxKeyId: number) => {
    if (editingInfluxKeyData) {
      // Update the row in influxKeys array
      const updatedInfluxKeys = influxKeys.map(ik => 
        ik.id === influxKeyId ? editingInfluxKeyData : ik
      );
      
      setInfluxKeys(updatedInfluxKeys);
      setEditingInfluxKeyId(null);
      setEditingInfluxKeyData(null);
      
      // Update formData with new Influx Keys
      setFormData(prev => ({
        ...prev,
        influxKeys: updatedInfluxKeys,
      }));
    }
  };

  const handleCancelInfluxKey = () => {
    // If canceling a new row (negative ID), remove it
    if (editingInfluxKeyId !== null && editingInfluxKeyId < 0) {
      const updatedInfluxKeys = influxKeys.filter(ik => ik.id !== editingInfluxKeyId);
      setInfluxKeys(updatedInfluxKeys);
      setFormData(prev => ({
        ...prev,
        influxKeys: updatedInfluxKeys,
      }));
    }
    setEditingInfluxKeyId(null);
    setEditingInfluxKeyData(null);
  };

  const handleDeleteInfluxKey = (influxKeyId: number) => {
    const updatedInfluxKeys = influxKeys.filter(ik => ik.id !== influxKeyId);
    setInfluxKeys(updatedInfluxKeys);
    
    // If we're deleting the row that's being edited, reset editing state
    if (editingInfluxKeyId === influxKeyId) {
      setEditingInfluxKeyId(null);
      setEditingInfluxKeyData(null);
    }
    
    // Update formData with new Influx Keys
    setFormData(prev => ({
      ...prev,
      influxKeys: updatedInfluxKeys,
    }));
  };

  const handleAddInfluxKey = () => {
    // Only allow adding if no row is currently being edited
    if (editingInfluxKeyId !== null) {
      return;
    }
    
    const newInfluxKey: InfluxKey = {
      id: nextTempId,
      keyInflux: "",
      isActive: true,
    };
    
    // Add new row at the beginning
    setInfluxKeys([newInfluxKey, ...influxKeys]);
    setEditingInfluxKeyId(nextTempId);
    setEditingInfluxKeyData({ ...newInfluxKey });
    setNextTempId(prev => prev - 1);
    
    // Update formData
    setFormData(prev => ({
      ...prev,
      influxKeys: [newInfluxKey, ...influxKeys],
    }));
  };

  const handleEditingInfluxKeyChange = (field: keyof InfluxKey, value: string | boolean) => {
    if (editingInfluxKeyData) {
      const updatedData = {
        ...editingInfluxKeyData,
        [field]: value,
      };
      
      // If keyInflux is being changed, update the ID dynamically
      if (field === "keyInflux" && typeof value === "string") {
        // Find the highest existing ID and add 1, or use the next available ID
        const maxId = influxKeys.reduce((max, ik) => {
          // Only consider positive IDs (not temporary negative ones)
          return ik.id > 0 && ik.id > max ? ik.id : max;
        }, 0);
        // If this is a new row (negative ID), assign a new positive ID
        // Otherwise keep the existing ID
        if (updatedData.id < 0) {
          updatedData.id = maxId + 1;
        }
      }
      
      setEditingInfluxKeyData(updatedData);
    }
  };

  // Get available keyInflux options from existing data
  const keyInfluxOptions = React.useMemo(() => {
    // Extract unique keyInflux values from existing influxKeys
    const existingKeys = influxKeys
      .map(ik => ik.keyInflux)
      .filter((key): key is string => Boolean(key) && key.trim() !== "");
    
    // Remove duplicates and sort
    const uniqueKeys = Array.from(new Set(existingKeys)).sort();
    
    // If we have existing keys, return them; otherwise return empty array
    // (You might want to add a default list here if needed)
    return uniqueKeys;
  }, [influxKeys]);

  const handleSave = async () => {
    if (!datacenter) return;
    try {
      setIsSaving(true);
      setErrorMessage(null);
      // Include provider name and influxKeys in the payload
      const payload = {
        ...formData,
        provider: selectedProvider,
        influxKeys: influxKeys,
      } as any;
      await onSave(payload);
      // If successful, modal will be closed by parent component
    } catch (error: any) {
      console.error("Error saving datacenter:", error);
      // Extract error message
      const errorMsg = error?.message || "An error occurred while updating the datacenter";
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const isStatusActive = formData.status === "Active";

  return (
    <>
      {/* Error Alert Modal */}
      <GenericAlert
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        onConfirm={() => setErrorMessage(null)}
        title="Error Updating Datacenter"
        description={errorMessage || ""}
        variant="error"
        confirmLabel="OK"
        hideCancel
      />

      <GenericModal 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Edit Datacenter"
        customWidth="max-w-7xl"
      >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="40%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        datacenter && (
          <div className="space-y-6">
            {/* Basic Information */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Basic Information
              </h3>
              <div className="grid grid-cols-3 gap-4 p-5">
                <GenericInput
                  label="Datacenter ID"
                  value={formData.datacenterId || ""}
                  onChange={(value) => handleInputChange("datacenterId", value)}
                />
                <GenericInput
                  label="Name"
                  value={formData.name || ""}
                  onChange={(value) => handleInputChange("name", value)}
                />
                <GenericInput
                  label="Display Name"
                  value={formData.displayName || ""}
                  onChange={(value) => handleInputChange("displayName", value)}
                />
                <GenericInput
                  label="Direction"
                  value={formData.direction || ""}
                  onChange={(value) => handleInputChange("direction", value)}
                />
                <GenericInput
                  label="City"
                  value={formData.city || ""}
                  onChange={(value) => handleInputChange("city", value)}
                />
                <GenericInput
                  label="GPS"
                  value={formData.gps || ""}
                  onChange={(value) => handleInputChange("gps", value)}
                />
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-semantic-text-secondary">
                    Status:
                  </label>
                  <GenericSwitch
                    checked={isStatusActive}
                    onChange={handleStatusToggle}
                  />
                  <span className="text-sm text-semantic-text-secondary">
                    {isStatusActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-semantic-text-secondary">
                    Visible:
                  </label>
                  <GenericSwitch
                    checked={formData.visible ?? true}
                    onChange={handleVisibleToggle}
                  />
                  <span className="text-sm text-semantic-text-secondary">
                    {formData.visible ? "Yes" : "No"}
                  </span>
                </div>
                <GenericInput
                  label="Number of Hardware"
                  value={String(datacenter.numberOfHardware ?? 0)}
                  readOnly
                />
              </div>
            </GenericCard>

            {/* Provider Information */}
            <GenericCard 
              isCollapsible={true}
              initialExpanded={false}
              headerContent={<h3 className="text-md font-semibold text-semantic-text-primary">Provider Information</h3>}
              className="p-5">
              <div className="grid grid-cols-3 gap-4 p-5">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Provider</label>
                  <GenericDropdown
                    items={providers.map((p) => p.name)}
                    defaultLabel={isLoadingProviders ? "Loading..." : "Select Provider"}
                    value={selectedProvider}
                    onSelect={handleProviderSelect}
                    disabled={isLoadingProviders}
                    maxWidthClass="w-full"
                  />
                </div>
                <GenericInput
                  label="Provider ID"
                  value={selectedProviderId !== null ? String(selectedProviderId) : ""}
                  onChange={() => {""}} // Read-only
                  readOnly
                />
                <GenericInput
                  label="Provider ID Addon"
                  value={selectedProviderIdAddon}
                  onChange={() => {""}} // Read-only
                  readOnly
                />
              </div>
            </GenericCard>

            {/* Capacity and Infrastructure */}
            <GenericCard
              isCollapsible={true}
              initialExpanded={false}
              headerContent={<h3 className="text-md font-semibold text-semantic-text-primary">Capacity and Infrastructure</h3>}
              className="p-5">
              {/* <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Capacity and Infrastructure
              </h3> */}
              <div className="grid grid-cols-3 gap-4 p-5">
                <GenericInput
                  label="Capacity (KW)"
                  value={formData.capacityKW || ""}
                  onChange={(value) => handleInputChange("capacityKW", value)}
                />
                <GenericInput
                  label="Phase"
                  value={formData.phase || ""}
                  onChange={(value) => handleInputChange("phase", value)}
                />
                <GenericInput
                  label="Cage"
                  value={formData.cage || ""}
                  onChange={(value) => handleInputChange("cage", value)}
                />
                <GenericInput
                  label="Rack ID"
                  value={formData.rackId || ""}
                  onChange={(value) => handleInputChange("rackId", value)}
                />
                <GenericInput
                  label="Rack Capacity"
                  value={formData.rackCapacity || ""}
                  onChange={(value) => handleInputChange("rackCapacity", value)}
                />
                <GenericInput
                  label="Patch Panel ODF"
                  value={formData.patchPanelODF || ""}
                  onChange={(value) => handleInputChange("patchPanelODF", value)}
                />
                <GenericInput
                  label="Rack Security Pin"
                  value={formData.rackSecurityPin || ""}
                  onChange={(value) => handleInputChange("rackSecurityPin", value)}
                />
              </div>
            </GenericCard>

            {/* Influx Keys */}
            <GenericCard isCollapsible={false} className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-semibold text-semantic-text-primary">
                  Influx Keys
                </h3>
                <GenericButton
                  label="Agregar"
                  variant="brand"
                  onClick={handleAddInfluxKey}
                  disabled={editingInfluxKeyId !== null}
                />
              </div>
              <GenericTable
                columns={[
                  {
                    header: "ID",
                    accessor: "id",
                    renderCell: (row) => {
                      // Show the ID dynamically - if it's being edited and has a keyInflux, show the assigned ID
                      const displayId = editingInfluxKeyId === row.id && editingInfluxKeyData?.keyInflux
                        ? (editingInfluxKeyData.id < 0 ? "New" : editingInfluxKeyData.id)
                        : (row.id < 0 ? "New" : row.id);
                      return (
                        <span className="text-sm text-semantic-text-secondary">
                          {displayId}
                        </span>
                      );
                    },
                  },
                  {
                    header: "Key Influx",
                    accessor: "keyInflux",
                    renderCell: (row) => {
                      if (editingInfluxKeyId === row.id) {
                        return (
                          <GenericDropdown
                            items={keyInfluxOptions}
                            value={editingInfluxKeyData?.keyInflux || ""}
                            defaultLabel="Select Key Influx"
                            onSelect={(value) => handleEditingInfluxKeyChange("keyInflux", value)}
                            minWidthClass="min-w-[200px]"
                            maxWidthClass="max-w-[300px]"
                            fixedPosition={true}
                            dropdownMaxHeightClass="max-h-48"
                          />
                        );
                      }
                      return <span className="text-sm text-semantic-text-secondary">{row.keyInflux || "-"}</span>;
                    },
                  },
                  {
                    header: "Status",
                    accessor: "isActive",
                    renderCell: (row) => {
                      if (editingInfluxKeyId === row.id) {
                        return (
                          <div className="flex items-center gap-2">
                            <GenericSwitch
                              checked={editingInfluxKeyData?.isActive ?? true}
                              onChange={(value) => handleEditingInfluxKeyChange("isActive", value)}
                            />
                            <span className="text-sm text-semantic-text-secondary">
                              {editingInfluxKeyData?.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        );
                      }
                      const variant = row.isActive ? "neutral" : "default";
                      return (
                        <GenericTag
                          variant={variant}
                          label={row.isActive ? "Active" : "Inactive"}
                          size="small"
                        />
                      );
                    },
                  },
                  {
                    header: "Actions",
                    accessor: "id",
                    renderCell: (row) => {
                      if (editingInfluxKeyId === row.id) {
                        return (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSaveInfluxKey(row.id)}
                              className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
                              title="Save"
                            >
                              <IconCheck className="w-5 h-5" />
                            </button>
                            <button
                              onClick={handleCancelInfluxKey}
                              className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
                              title="Cancel"
                            >
                              <IconCancel className="w-5 h-5" />
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditInfluxKey(row.id)}
                            className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
                            title="Edit"
                          >
                            <IconEdit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteInfluxKey(row.id)}
                            className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-red-50 hover:border-red-300 hover:text-red-600 w-8 h-8"
                            title="Delete"
                          >
                            <IconDelete className="w-5 h-5" />
                          </button>
                        </div>
                      );
                    },
                  },
                ]}
                data={influxKeys}
                width="100%"
                initialRowsPerPage={10}
                rowsPerPageOptions={[10, 20, 25]}
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight="300px"
                minTableBodyHeight="120px"
              />
            </GenericCard>
          </div>
        )
      )}

      <div className="flex justify-end gap-2 mt-6">
        <GenericButton 
          label="Cancel" 
          variant="secondary" 
          onClick={onClose}
          disabled={isSaving}
        />
        <GenericButton 
          label={isSaving ? "Saving..." : "Save"} 
          variant="brand" 
          onClick={handleSave}
          disabled={isSaving || isLoading}
        />
      </div>
    </GenericModal>
    </>
  );
};
